import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import DashboardLayout from "@/components/DashboardLayout";
import { stageMeta, stageLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Users, ClipboardList, Boxes, Scale, Droplets, CheckCircle2,
  UserPlus, PackagePlus, ListOrdered, Monitor, ArrowRight,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  const cards = stats ? [
    { key: "stat_clients", value: stats.total_clients, icon: Users, color: "#3B82F6" },
    { key: "stat_active", value: stats.active_orders, icon: ClipboardList, color: "#D4A373" },
    { key: "stat_crates", value: stats.total_crates, icon: Boxes, color: "#8B5CF6" },
    { key: "stat_weight", value: `${stats.total_weight} kg`, icon: Scale, color: "#F59E0B" },
    { key: "stat_yield", value: `${stats.estimated_yield} L`, icon: Droplets, color: "#3D522B" },
    { key: "stat_completed", value: stats.completed_orders, icon: CheckCircle2, color: "#059669" },
  ] : [];

  const quick = [
    { label: "qa_new_client", icon: UserPlus, to: "/clients", testid: "qa-new-client" },
    { label: "qa_new_order", icon: PackagePlus, to: "/clients", testid: "qa-new-order" },
    { label: "qa_queue", icon: ListOrdered, to: "/queue-management", testid: "qa-queue" },
    { label: "qa_live", icon: Monitor, to: `/live-queue/${user?.slug}`, testid: "qa-live" },
  ];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl space-y-8" data-testid="dashboard-page">
        <div className="animate-fade-up">
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("dash_welcome")}, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="mt-1 text-muted-foreground">{t("dash_overview")}</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {cards.map((c, i) => (
            <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-xl border border-border bg-card p-4 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${c.color}1a`, color: c.color }}>
                <c.icon className="h-[18px] w-[18px]" />
              </div>
              <p className="mt-3 font-heading text-2xl font-bold">{c.value}</p>
              <p className="text-xs text-muted-foreground">{t(c.key)}</p>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div>
          <h2 className="mb-3 font-heading text-lg font-semibold">{t("quick_actions")}</h2>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {quick.map((q) => (
              <button
                key={q.label}
                data-testid={q.testid}
                onClick={() => navigate(q.to)}
                className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-olive-border hover:shadow-md"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-olive-light text-olive transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <q.icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-semibold">{t(q.label)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          {/* Recent orders */}
          <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold">{t("recent_orders")}</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate("/queue-management")} data-testid="view-all-orders-btn">
                {t("nav_queue")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
            {stats?.recent_orders?.length ? (
              <div className="space-y-2">
                {stats.recent_orders.map((o) => {
                  const si = (user?.stages || []).findIndex((s) => s.id === o.status);
                  const stage = (user?.stages || [])[si] || { id: o.status, label: o.status };
                  const meta = stageMeta(o.status, si < 0 ? 0 : si);
                  const Icon = meta.icon;
                  return (
                    <div key={o.id} data-testid={`recent-order-${o.order_code}`} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${meta.color}1a`, color: meta.color }}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div>
                          <p className="text-sm font-semibold">{o.client_name}</p>
                          <p className="font-mono text-xs text-muted-foreground">{o.order_code} · {o.crate_count} {t("crates")} · {o.total_weight} kg</p>
                        </div>
                      </div>
                      <span className="rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: `${meta.color}1a`, color: meta.color }}>
                        {stageLabel(t, stage)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : <p className="py-8 text-center text-sm text-muted-foreground">{t("no_orders")}</p>}
          </div>

          {/* Pipeline overview */}
          <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold">{t("stage_overview")}</h2>
            <div className="space-y-3">
              {(user?.stages || []).map((s, si) => {
                const meta = stageMeta(s.id, si);
                const Icon = meta.icon;
                const count = stats?.stage_counts?.[s.id] ?? 0;
                const total = stats ? Object.values(stats.stage_counts).reduce((a, b) => a + b, 0) || 1 : 1;
                return (
                  <div key={s.id} data-testid={`pipeline-${s.id}`}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2"><Icon className="h-4 w-4" style={{ color: meta.color }} /> {stageLabel(t, s)}</span>
                      <span className="font-mono font-semibold">{count}</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full transition-all" style={{ width: `${(count / total) * 100}%`, background: meta.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
